import React, { useState, useEffect } from 'react';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import useAuthStore from '../../store/useAuthStore';

const PollComponent = ({ data, messageId, chatId }) => {
    const { user } = useAuthStore();
    const [votesMap, setVotesMap] = useState({});

    useEffect(() => {
        if (!messageId || !chatId) return;

        const unsub = onSnapshot(doc(db, 'chats', chatId, 'messages', messageId), (docSnap) => {
            if (docSnap.exists()) {
                const msgData = docSnap.data();
                setVotesMap(msgData.pollVotes || {});
            }
        });
        return () => unsub();
    }, [chatId, messageId]);

    const handleVote = async (option) => {
        if (!user) return;

        const newVotes = { ...votesMap, [user.uid]: option };

        try {
            await updateDoc(doc(db, 'chats', chatId, 'messages', messageId), {
                pollVotes: newVotes
            });
        } catch (err) {
            console.error("Failed to vote:", err);
        }
    };

    const totalVotes = Object.keys(votesMap).length;
    const counts = {};
    data.options.forEach(opt => counts[opt] = 0);
    Object.values(votesMap).forEach(val => {
        if (counts[val] !== undefined) counts[val]++;
    });

    const userVote = votesMap[user?.uid] || '';

    return (
        <div className="mt-2 mb-2 p-5 bg-[var(--bg-secondary)] border border-[var(--border-subtle)]/50 rounded-2xl w-full max-w-sm sm:max-w-md shadow-sm">
            <h4 className="text-[15px] font-bold text-[var(--text-main)] mb-4 flex items-start gap-2 leading-snug">
                <span className="text-xl">📊</span>
                {data.question}
            </h4>

            <div className="space-y-3">
                {data.options.map((option) => {
                    const count = counts[option] || 0;
                    const percent = totalVotes > 0 ? (count / totalVotes) * 100 : 0;
                    const isSelected = userVote === option;

                    return (
                        <div key={option} className="flex flex-col">
                            <label className="flex items-center gap-3 cursor-pointer group mb-1.5 focus:outline-none">
                                <div className="relative flex items-center justify-center shrink-0">
                                    <input
                                        type="radio"
                                        name={`poll-${messageId}`}
                                        value={option}
                                        checked={isSelected}
                                        onChange={() => handleVote(option)}
                                        className="peer appearance-none w-5 h-5 border-2 border-[var(--border-subtle)] rounded-full checked:border-[var(--accent-primary)] transition-colors focus:ring-2 focus:ring-[var(--accent-primary)]/30 focus:outline-none cursor-pointer group-hover:border-[var(--text-muted)]"
                                    />
                                    {isSelected && (
                                        <div className="absolute w-2.5 h-2.5 bg-[var(--accent-primary)] rounded-full pointer-events-none" />
                                    )}
                                </div>
                                <span className={`text-sm font-medium transition-colors ${isSelected ? 'text-[var(--accent-primary)]' : 'text-[var(--text-muted)] group-hover:text-[var(--text-main)]'}`}>
                                    {option}
                                </span>
                            </label>

                            <div className="flex items-center gap-3 pl-8">
                                <div className="flex-1 h-1.5 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all duration-500 ${isSelected ? 'bg-[var(--accent-primary)]' : 'bg-[var(--text-muted)]/50'}`}
                                        style={{ width: `${percent}%` }}
                                    />
                                </div>
                                <span className={`text-[10px] sm:text-xs font-bold w-8 text-right ${isSelected ? 'text-[var(--accent-primary)]' : 'text-[var(--text-muted)]'}`}>
                                    {Math.round(percent)}%
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-5 pt-3 border-t border-[var(--border-subtle)]/50 flex justify-end">
                <span className="text-xs font-medium text-[var(--text-muted)]">
                    {totalVotes} vote{totalVotes !== 1 ? 's' : ''}
                </span>
            </div>
        </div>
    );
};

export default PollComponent;
